// Version check — independent "a newer build is deployed" signal.
//
// The SW polls for a new service worker file, but that can stall behind
// aggressive HTTP/CDN caching. This module is the belt to that suspenders:
// fetch /version.json (emitted at build time, served no-store from CF edge)
// and compare buildId (commit SHA) against the compiled-in window.BUILD_ID.
//
// Checks fire on: page load, every 5 minutes, visibilitychange → visible,
// and when the network comes back online.
//
// onUpdate fires at most once per newly-seen remote buildId so the same
// toast can't stack.

const POLL_INTERVAL_MS = 5 * 60 * 1000;

export async function fetchRemoteVersion() {
  try {
    // KAN-931: /api/version is Worker-native (not a static asset), so CF returns
    // cf-cache-status: DYNAMIC — the query-string cache-buster is now effective.
    const res = await fetch('/api/version?t=' + Date.now(), { cache: 'no-store' });
    if (!res.ok) return null;
    const json = await res.json();
    if (!json || typeof json.buildId !== 'string' || typeof json.buildTime !== 'string') return null;
    return json;
  } catch {
    return null;
  }
}

export function isUpdateAvailable(remote) {
  if (!remote) return false;
  const localId   = window.BUILD_ID;
  const localTime = window.BUILD_TIME;
  if (!remote.buildId   || remote.buildId   === 'dev') return false;
  if (!localId          || localId           === 'dev') return false;
  if (remote.buildId === localId) return false;
  // Guard against rollbacks or local builds being "newer"
  if (!remote.buildTime || !localTime) return false;
  return remote.buildTime > localTime;
}

export function startVersionPolling(onUpdate) {
  let notifiedBuildId = null;
  let stopped = false;

  const check = async () => {
    if (stopped) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    const remote = await fetchRemoteVersion();
    if (stopped || !remote || !isUpdateAvailable(remote)) return;
    if (notifiedBuildId === remote.buildId) return;
    notifiedBuildId = remote.buildId;
    onUpdate(remote.version || '');
  };

  const onOnline    = () => void check();
  const onVisible   = () => { if (document.visibilityState === 'visible') void check(); };

  void check();
  const interval = window.setInterval(() => void check(), POLL_INTERVAL_MS);
  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisible);

  return () => {
    stopped = true;
    window.clearInterval(interval);
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisible);
  };
}
